import { h, Fragment } from 'preact';
import ChevronDown from 'utils/icon/chevron-down.svg';
import Close from 'utils/icon/close.svg';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import updateURL from '../utils/update-url';

const CountrySelectorModal = ({
  countries,
  languages,
  onClose,
  message,
  hreflangUrl,
  geoLocation = {},
  defaultCountry = {},
  defaultLanguage = {},
  hideCloseBtnSetting,
}) => {
  const [country, setCountry] = useState(defaultCountry);
  const [language, setLanguage] = useState(defaultLanguage);

  const isDifferentStore = useMemo(() => {
    const { host } = new URL(country.link);
    return host !== window.location.host;
  }, [country]);

  const sortedLanguages = useMemo(
    () => [
      ...languages.filter((item) =>
        ['en', 'fr'].includes(item.iso_code.toLowerCase().trim()),
      ),
      ...languages
        .filter(
          (item) => !['en', 'fr'].includes(item.iso_code.toLowerCase().trim()),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    ],
    [languages],
  );

  useEffect(() => {
    // This effect was calculating header position but not using it
    // Keeping the effect in case it's needed for future functionality
  }, []);

  useEffect(() => {
    const isCountrySelected = (document.cookie.match(
      '(^|; )geo_selected=([^;]*)',
    ) || 0)[2];
    if (!isCountrySelected) {
      if (geoLocation.country) {
        const detectedCountry = countries.find((item) =>
          item.iso_code.find(
            (code) =>
              code.toLowerCase().trim() === geoLocation.country.toLowerCase(),
          ),
        );
        if (detectedCountry) {
          setCountry(detectedCountry);
        }
      }
      if (geoLocation.language) {
        const detectedLanguage = languages.find(
          (item) =>
            item.iso_code.toLowerCase() === geoLocation.language.toLowerCase(),
        );
        if (detectedLanguage) {
          setLanguage(detectedLanguage);
        }
      }
    }
  }, [geoLocation]);

  const optionChangeHandle = (value, type) => {
    const item = (type === 'country' ? countries : languages).find(
      (i) => i.name === value,
    );
    if (item) {
      if (type === 'country') {
        setCountry({ ...item });
      } else {
        setLanguage({ ...item });
      }
    }
  };
  const clickHandle = useCallback(() => {
    const targetCountry = country || countries[0];
    const targetLanguage = language || languages[0];
    const { hostname } = new URL(targetCountry.link);
    let link = `/`;
    if (
      !targetCountry.iso_code.some((code) =>
        ['us', 'ca'].includes(code.toLowerCase().trim()),
      ) &&
      (targetCountry.iso_code.length < 2 ||
        targetCountry.iso_code.some(
          (code) =>
            code.toLowerCase().trim() === geoLocation?.country?.toLowerCase(),
        ))
    ) {
      if (targetCountry.iso_code.length > 1) {
        link += `${targetLanguage.iso_code}-${geoLocation.country}`;
      } else {
        link += `${targetLanguage.iso_code}-${targetCountry.iso_code}/`;
      }
    } else if (targetLanguage.iso_code.toLowerCase() !== 'en') {
      link += `${targetLanguage.iso_code}/`;
    }
    if (isDifferentStore) {
      if (hreflangUrl) {
        link = `https://${hostname}${link}${hreflangUrl}`;
      } else {
        link = window.location.pathname.replace(
          Shopify.routes.root,
          `https://${hostname}${link}`,
        );
      }
    } else {
      link = window.location.pathname.replace(Shopify.routes.root, link);
    }
    const expire = new Date(Date.now() + 300 * 864e5).toUTCString();
    document.cookie = `geo_selected=1; expires=${expire}; path=/`;

    Shopify.analytics.publish('country_selector_save', {
      country: `${country?.name}`,
      language: `${language?.name}`,
    });

    window.location = updateURL(`${link.toLocaleLowerCase()}`, { geo: '1' });
  }, [country, language]);

  return (
    <>
      <div
        className="fixed z-[1000] inset-0 flex justify-center items-center lg:bg-black/70 lg:!pt-0"
      >
        <div className="absolute inset-0 z-1" role="presentation"></div>

        <div className="bg-white relative z-10 w-full h-full flex-1 flex flex-col min-h-0 lg:max-w-[928px] lg:max-h-[621px]">
          {!hideCloseBtnSetting && (
            <div className={`flex h-[50px] lg:h-[60px] lg:justify-end`}>
              <button
                onClick={onClose}
                className="flex gap-3.5 px-4 items-center lg:hidden"
              >
                <ChevronDown className="rotate-90" />
                <span className="text-headline-tag">
                  {window.TMW.Strings.countrySelector.back}
                </span>
              </button>
              <button
                onClick={onClose}
                className="hidden lg:flex w-[60px] justify-center items-center text-black lg:absolute top-1 z-1 right-0 lg:top-0 lg:w-[60px] lg:h-[60px]"
              >
                <Close />
              </button>
            </div>
          )}
          <div
            // eslint-disable-next-line max-len
            className={`hidden absolute top-0 left-1/2 h-full z-3 w-[1px] bg-grey-4 lg:block ${
              hideCloseBtnSetting
                ? 'lg:h-100% lg:top-[0px]'
                : 'lg:h-[calc(100%-60px)] lg:top-[60px]'
            }`}
          ></div>
          <div className="border-y border-grey-4 flex-1 min-h-0	flex flex-col lg:grid lg:grid-cols-2 lg:relative">
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="bg-white z-2 p-5 lg:px-[30px] border-b border-grey-4  uppercase text-headline-tag lg:border-t-0 lg:sticky top-0">
                {window.TMW.Strings.countrySelector.shipTo}: {country.name}
              </div>
              <div className="p-5 flex flex-col lg:px-[30px] flex-1 min-h-0 overflow-y-auto gap-3.5 lg:pt-0 lg:h-max lg:mt-5">
                {countries.map((item) => (
                  <label
                    key={item.name}
                    className="country-selector-label relative flex gap-1.5 items-center"
                  >
                    <input
                      type="radio"
                      className="opacity-0 absolute w-0"
                      name="country"
                      checked={country.name === item.name}
                      value={item.name}
                      onChange={(e) =>
                        optionChangeHandle(e.target.value, 'country')
                      }
                    />
                    <span className="w-[19px] h-[19px] rounded-full border border-grey-2 flex justify-center items-center ring-offset-2">
                      {country.name === item.name && (
                        <span className="w-[11px] h-[11px] bg-black rounded-full"></span>
                      )}
                    </span>
                    <span className="text-body-2">{item.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="bg-white z-2 p-5 lg:px-[30px] border-y border-grey-4 uppercase text-headline-tag lg:sticky lg:border-t-0 top-0">
                {window.TMW.Strings.countrySelector.language}: {language.name}
              </div>
              <div className="p-5 lg:px-[30px] flex flex-col flex-1 min-h-0 overflow-y-auto gap-3.5 lg:pt-0 lg:mt-5">
                {sortedLanguages.map((item) => (
                  <label
                    key={item.iso_code}
                    className="country-selector-label relative flex gap-1.5 items-center"
                  >
                    <input
                      type="radio"
                      className="opacity-0 absolute w-0"
                      name="country"
                      checked={item.iso_code === language.iso_code}
                      value={item.name}
                      onChange={(e) =>
                        optionChangeHandle(e.target.value, 'language')
                      }
                    />
                    <span className="w-[19px] h-[19px] rounded-full border border-grey-2 flex justify-center items-center  ring-offset-2">
                      {language.iso_code === item.iso_code && (
                        <span className="w-[11px] h-[11px] bg-black rounded-full"></span>
                      )}
                    </span>
                    <span className="text-body-2 capitalize">{item.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-white pt-5 min-h-[114px] relative z-10 flex justify-end items-center lg:min-h-0 lg:p-5  flex-col lg:justify-center ">
            {message && isDifferentStore && (
              <div
                className="text-body-2 px-5 mb-5 text-center max-w-3xl"
                dangerouslySetInnerHTML={{ __html: message }}
              ></div>
            )}
            <button
              onClick={clickHandle}
              className="button w-full button--primary lg:max-w-[317px]"
            >
              {window.TMW.Strings.countrySelector.saveSetting}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CountrySelectorModal;
